import { updateApplication } from '@otter-ai/recall-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userProfileQueryCreator } from '@/query/userProfileQueryCreator';
import { getConnectedAppsQueryCreator } from '../query/getConnectedAppsQueryCreator';

export type ApplicationDisableResponse = {
  message: string;
};

/**
 * Generic hook to disconnect an application by app name.
 * This hook only disconnects (sets enabled to false), it does not toggle.
 *
 * @example
 * ```tsx
 * const { mutateAsync: disconnectApp, isPending } = useDisconnectApplication('google-drive');
 * await disconnectApp();
 * ```
 */
export function useDisconnectApplication(appName: string) {
  const queryClient = useQueryClient();

  return useMutation<ApplicationDisableResponse, Error, void>({
    mutationFn: async () => {
      const res = await updateApplication({
        path: { app_name: appName },
        body: { enabled: false },
      });
      if (res.error) {
        throw new Error(`Failed to disconnect application ${appName}`);
      }
      const data = res.data;
      const message =
        data && 'message' in data && typeof data.message === 'string'
          ? data.message
          : 'OK';
      return { message };
    },
    onSuccess: async () => {
      // Invalidate related queries to refresh the UI
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['applications'] }),
        queryClient.invalidateQueries(userProfileQueryCreator()),
        queryClient.invalidateQueries(getConnectedAppsQueryCreator()),
      ]);
    },
  });
}
